import React, { useState, useEffect, useMemo } from 'react';
import {
  X, TrendingUp, TrendingDown, Clock, Target, DollarSign,
  Briefcase, Settings, RefreshCw, MessageSquare, Activity, BarChart3, ChevronRight,
  FlaskConical, History
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, Cell, ReferenceLine,
} from 'recharts';
import { apiService } from '../../services/apiService';
import { ForumPost, SignificanceResult, IdentityHistory, AttributionResult, AgentEvolution } from '../../types';
import { significanceBadgeMeta, formatPValue, formatCI, formatTradesToSignificance } from '../../utils/significance';
import { sortBuckets, winRateColor, prettyBucketKey, hasAttributionData, DIMENSION_LABELS } from '../../utils/attribution';
import { extractSeries, metricLabel, metricTrend, hasEvolutionData } from '../../utils/evolution';
import { debateInfluenceSummary, agentDebateBadge } from '../../utils/debate';
import { formatDateWithYear } from '../../utils/format';
import NavChart from './NavChart';
import Scoreboard from './Scoreboard';
import Modal from '../ui/Modal';

interface AgentDetailModalProps {
  agentId: string;
  onClose: () => void;
}

interface Position {
  id: string;
  symbol: string;
  strategy: string;
  strike: number;
  expiration: string;
  entry_price: number;
  current_price?: number;
  contracts: number;
  status: string;
  unrealized_pnl: number;
  unrealized_pnl_pct: number;
  unrealized_roi_pct?: number;
  realized_pnl: number;
  realized_roi_pct?: number;
  realized_premium_pct?: number;
  entry_date: string;
  exit_date?: string;
  entry_reason: string;
  exit_reason?: string;
  buying_power_used: number;
  stock_entry_price?: number;
  cost_basis?: number;
  current_stock_price?: number;
  exit_stock_price?: number;
  exit_price?: number;
}

export const AgentDetailModal: React.FC<AgentDetailModalProps> = ({ agentId, onClose }) => {
  const [agentData, setAgentData] = useState<any>(null);
  const [forumPosts, setForumPosts] = useState<ForumPost[]>([]);
  const [significance, setSignificance] = useState<SignificanceResult | null>(null);
  const [identityHistory, setIdentityHistory] = useState<IdentityHistory | null>(null);
  const [attribution, setAttribution] = useState<AttributionResult | null>(null);
  const [evolution, setEvolution] = useState<AgentEvolution | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'performance' | 'positions' | 'forum' | 'identity' | 'rules'>('overview');
  const [positionFilter, setPositionFilter] = useState<'all' | 'open' | 'closed'>('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [data, forum, sig, identity, attrib, evo] = await Promise.all([
          apiService.getAgentDetail(agentId),
          apiService.getForumFeed(20).catch(() => ({ posts: [] })),
          apiService.getAgentSignificance(agentId).catch(() => null),
          apiService.getAgentIdentityHistory(agentId).catch(() => null),
          apiService.getAttribution(agentId).catch(() => null),
          apiService.getAgentEvolution(agentId).catch(() => null),
        ]);
        setAgentData(data);
        setSignificance(sig);
        setIdentityHistory(identity);
        setAttribution(attrib);
        setEvolution(evo);
        const agentPosts = (forum?.posts || []).filter(
          (p: ForumPost) => p.messages?.some((m: any) => m.agent_id === agentId)
        );
        setForumPosts(agentPosts);
      } catch (err) {
        console.error('Failed to load agent:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [agentId]);

  if (loading) {
    return (
      <Modal onClose={onClose} size="4xl">
        <div className="p-8">
          <RefreshCw className="w-8 h-8 animate-spin text-cyan-400 mx-auto" />
        </div>
      </Modal>
    );
  }

  if (!agentData) return null;

  const { agent, performance, open_positions, closed_positions } = agentData;
  const all_positions = [...(open_positions || []), ...(closed_positions || [])];
  const nav = performance.nav ?? agent.current_capital;
  const totalReturn = performance.total_return_pct ?? performance.total_pnl_pct ?? 0;
  const isProfit = totalReturn >= 0;

  const getRiskBadge = (risk: string) => {
    switch (risk) {
      case 'conservative': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'aggressive': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    }
  };

  const getTierBadge = (tier: string) => {
    switch (tier) {
      case 'platform': return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
      case 'premium': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: BarChart3 },
    { id: 'performance' as const, label: 'Performance', icon: Activity },
    { id: 'positions' as const, label: `Positions (${all_positions.length})`, icon: Briefcase },
    { id: 'forum' as const, label: `Forum (${forumPosts.length})`, icon: MessageSquare },
    { id: 'identity' as const, label: 'Identity', icon: History },
    { id: 'rules' as const, label: 'Rules', icon: Settings },
  ];

  return (
    <Modal onClose={onClose} size="4xl">
        {/* Header */}
        <div className="flex items-center justify-between p-3 md:p-5 border-b border-slate-700/50">
          <div className="flex items-center gap-2 md:gap-4 min-w-0">
            <div className="text-3xl md:text-5xl shrink-0">{agent.emoji}</div>
            <div>
              <h2 className="text-base md:text-xl font-bold text-white truncate">{agent.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-10 px-2 py-0.5 rounded-full border ${getRiskBadge(agent.risk_level)}`}>
                  {agent.risk_level.toUpperCase()}
                </span>
                <span className={`text-10 px-2 py-0.5 rounded-full border ${getTierBadge(agent.tier)}`}>
                  {agent.tier.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
          <div className="text-right mr-12">
            <div className="text-10 text-slate-500 uppercase tracking-wider">Net Asset Value</div>
            <div className={`text-2xl font-bold tabular-nums ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
              ${nav.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </div>
            <div className={`text-xs tabular-nums ${isProfit ? 'text-green-400/70' : 'text-red-400/70'}`}>
              {isProfit ? '+' : '-'}{Math.abs(totalReturn).toFixed(2)}% NAV
            </div>
            {(() => {
              const realizedPct = performance.total_pnl_pct ?? 0;
              const rp = realizedPct >= 0;
              return (
                <div className={`text-xs tabular-nums ${rp ? 'text-green-400/70' : 'text-red-400/70'}`}>
                  {rp ? '+' : '-'}{Math.abs(realizedPct).toFixed(2)}% Realized P&L
                </div>
              );
            })()}
          </div>
          <button onClick={onClose} className="absolute top-5 right-5 p-2 hover:bg-slate-700 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Personality + Archetype */}
        <div className="px-5 py-3 bg-slate-900/50 border-b border-slate-700/30">
          {agent.archetype && (
            <div className="flex items-center gap-2 mb-1">
              <span className="text-10 px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 font-medium">
                {agent.archetype}
              </span>
            </div>
          )}
          <p className="text-xs text-slate-400 italic leading-relaxed">"{agent.personality}"</p>
          {agent.inspired_by && (
            <p className="text-10 text-slate-500 mt-1">Inspired by: {agent.inspired_by}</p>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-700/30">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex-1 flex flex-col md:flex-row items-center justify-center gap-0.5 md:gap-1.5 py-2 md:py-2.5 min-h-[44px] text-9 md:text-xs font-medium touch-manipulation transition-all ${
                activeTab === id
                  ? 'text-cyan-400 border-b-2 border-cyan-400 bg-cyan-500/5'
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 md:p-5">
          {activeTab === 'overview' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={<DollarSign className="w-4 h-4 text-green-400" />} label="Realized P&L">
                  <div className={`text-xl font-bold tabular-nums ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
                    {isProfit ? '+' : '-'}${Math.abs(performance.total_pnl).toFixed(0)}
                  </div>
                  <div className={`text-10 tabular-nums ${(performance.total_pnl_pct ?? 0) >= 0 ? 'text-green-400/60' : 'text-red-400/60'}`}>
                    {(performance.total_pnl_pct ?? 0) >= 0 ? '+' : '-'}{Math.abs(performance.total_pnl_pct ?? 0).toFixed(2)}%
                  </div>
                </StatCard>
                <StatCard icon={<Target className="w-4 h-4 text-yellow-400" />} label="Win Rate">
                  <div className="text-xl font-bold text-white">{performance.win_rate.toFixed(1)}%</div>
                  <div className="text-10 text-slate-500">{performance.winning_trades}W / {performance.losing_trades}L</div>
                </StatCard>
                <StatCard icon={<Activity className="w-4 h-4 text-cyan-400" />} label="Trades">
                  <div className="text-xl font-bold text-white">{performance.total_trades}</div>
                  <div className="text-10 text-cyan-400/70">{performance.open_positions} open</div>
                </StatCard>
                <StatCard icon={<Clock className="w-4 h-4 text-purple-400" />} label="Cash">
                  <div className="text-xl font-bold text-white">${(agent.current_capital / 1000).toFixed(1)}k</div>
                  <div className="text-10 text-slate-500">of ${(agent.starting_capital / 1000).toFixed(0)}k</div>
                </StatCard>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-green-500/5 border border-green-500/20 rounded-xl">
                  <div className="text-10 text-green-400 uppercase tracking-wider mb-1">Best Trade</div>
                  <div className="text-lg font-bold text-green-400 tabular-nums">+${(performance.best_trade_pnl || 0).toFixed(0)}</div>
                </div>
                <div className="p-3 bg-red-500/5 border border-red-500/20 rounded-xl">
                  <div className="text-10 text-red-400 uppercase tracking-wider mb-1">Worst Trade</div>
                  <div className="text-lg font-bold text-red-400 tabular-nums">-${Math.abs(performance.worst_trade_pnl || 0).toFixed(0)}</div>
                </div>
              </div>
              <div>
                <div className="text-10 text-slate-500 uppercase tracking-wider mb-1">Strategy</div>
                <p className="text-sm text-slate-300 leading-relaxed">{agent.description}</p>
              </div>
              {agent.tags && agent.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {agent.tags.map((tag: string, i: number) => (
                    <span key={i} className="text-10 px-2 py-1 bg-slate-700/50 text-slate-400 rounded-full border border-slate-600/30">{tag}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'performance' && (
            <div className="space-y-5">
              <NavChart agentId={agentId} startingCapital={agent.starting_capital} />

              {/* B-015: this agent vs SPY, QQQ, JEPI, QYLD and the YieldMax fund */}
              <Scoreboard agentId={agentId} />
              
              {/* Additional performance stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-800/40 rounded-xl border border-slate-700/20">
                  <div className="text-10 text-slate-500 uppercase tracking-wider mb-1">Max Drawdown</div>
                  <div className="text-lg font-bold text-red-400 tabular-nums">
                    {performance.max_drawdown_pct ? `-${performance.max_drawdown_pct.toFixed(2)}%` : '—'}
                  </div>
                  {performance.max_drawdown ? (
                    <div className="text-10 text-slate-500 tabular-nums">-${performance.max_drawdown.toFixed(0)}</div>
                  ) : null}
                </div>
                <div className="p-3 bg-slate-800/40 rounded-xl border border-slate-700/20">
                  <div className="text-10 text-slate-500 uppercase tracking-wider mb-1">Sharpe Ratio</div>
                  <div className={`text-lg font-bold tabular-nums ${
                    (performance.sharpe_ratio || 0) > 1 ? 'text-green-400' : (performance.sharpe_ratio || 0) > 0 ? 'text-yellow-400' : 'text-slate-400'
                  }`}>
                    {performance.sharpe_ratio ? performance.sharpe_ratio.toFixed(2) : '—'}
                  </div>
                  {performance.sortino_ratio ? (
                    <div className="text-10 text-slate-500 tabular-nums">Sortino: {performance.sortino_ratio.toFixed(2)}</div>
                  ) : null}
                </div>
              </div>

              {/* Statistical Significance (Spec 2.2) */}
              <SignificanceCard significance={significance} />

              {/* Performance Attribution (Spec 2.5) */}
              <AttributionCard attribution={attribution} />

              {/* Agent Evolution (Spec 2.4) */}
              <EvolutionCard evolution={evolution} />

              {/* P&L Distribution Bar Chart */}
              <PnlDistributionChart closedPositions={closed_positions || []} />

              {/* Drawdown Chart */}
              <DrawdownChart agentId={agentId} />
            </div>
          )}

          {activeTab === 'identity' && (
            <IdentityHistoryView identity={identityHistory} />
          )}

          {activeTab === 'positions' && (
            <div className="space-y-3">
              {/* Filter */}
              <div className="flex gap-1.5">
                {(['all', 'open', 'closed'] as const).map((f) => {
                  const count = f === 'all'
                    ? all_positions.length
                    : f === 'open'
                    ? all_positions.filter(p => p.status === 'open').length
                    : all_positions.filter(p => p.status === 'closed').length;
                  return (
                    <button
                      key={f}
                      onClick={() => setPositionFilter(f)}
                      className={`text-10 px-2.5 py-1 rounded-full font-medium transition-all ${
                        positionFilter === f
                          ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                          : 'bg-slate-800/40 text-slate-500 border border-slate-700/30 hover:text-slate-300'
                      }`}
                    >
                      {f.charAt(0).toUpperCase() + f.slice(1)} ({count})
                    </button>
                  );
                })}
              </div>
              {all_positions.filter(p => {
                if (positionFilter === 'all') return true;
                return p.status === positionFilter;
              }).length === 0 ? (
                <EmptyState icon={<Briefcase className="w-10 h-10 text-slate-600" />} message="No positions" />
              ) : (
                all_positions.filter(p => {
                  if (positionFilter === 'all') return true;
                  return p.status === positionFilter;
                }).map((pos: Position) => {
                  const isClosed = pos.status === 'closed';
                  const isShares = pos.strategy === 'SHARES';
                  const pnl = isClosed ? pos.realized_pnl : (pos.unrealized_pnl || 0);
                  const pnlPct = positionPnlPct(pos);
                  const isPosProfit = pnl >= 0;
                  return (
                    <div key={pos.id} className={`p-4 ${isClosed ? 'bg-slate-800/20 opacity-70' : 'bg-slate-800/40'} border border-slate-700/30 rounded-xl`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-white">{pos.symbol}</span>
                          <span className={`text-10 px-1.5 py-0.5 rounded font-medium ${
                            pos.strategy === 'SHARES' ? 'bg-purple-500/15 text-purple-400'
                            : 'bg-cyan-500/15 text-cyan-400'
                          }`}>
                            {pos.strategy === 'Cash-Secured Put' ? 'CSP' : pos.strategy === 'Covered Call' ? 'CC' : pos.strategy === 'SHARES' ? 'Shares' : pos.strategy}
                          </span>
                          {isClosed
                            ? <span className="text-10 md:text-9 px-1.5 py-0.5 bg-red-500/15 text-red-400 border border-red-500/30 rounded font-bold">CLOSED</span>
                            : <span className="text-10 md:text-9 px-1.5 py-0.5 bg-green-500/15 text-green-400 border border-green-500/30 rounded font-bold">OPEN</span>
                          }
                          <span className="text-10 text-slate-500">
                            {isShares
                              ? `${pos.contracts * 100} shares @ $${(pos.stock_entry_price || 0).toFixed(2)}`
                              : `${pos.contracts}x @ $${pos.entry_price.toFixed(2)}`
                            }
                          </span>
                        </div>
                        <div className={`text-base font-bold tabular-nums ${isPosProfit ? 'text-green-400' : 'text-red-400'}`}>
                          {isPosProfit ? '+' : '-'}${Math.abs(pnl).toFixed(0)}
                          <span className="text-xs ml-1">({pnlPct === null ? '—' : `${isPosProfit ? '+' : '-'}${Math.abs(pnlPct).toFixed(1)}%`})</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                        {isShares ? (
                          <>
                            <Detail label="Entry Price" value={`$${(pos.stock_entry_price || 0).toFixed(2)}`} />
                            <Detail label="Current" value={pos.current_stock_price ? `$${pos.current_stock_price.toFixed(2)}` : '—'} />
                            <Detail label="Shares" value={`${pos.contracts * 100}`} />
                            <Detail label="Expiry" value="N/A" />
                            <Detail label="BP Used" value={`$${(pos.buying_power_used / 1000).toFixed(1)}k`} />
                          </>
                        ) : (
                          <>
                            <Detail label="Strike" value={`$${pos.strike}`} />
                            <Detail label="Expiry" value={pos.expiration} />
                            <Detail label="Entry" value={`$${pos.entry_price.toFixed(2)}`} />
                            <Detail label="Current" value={pos.current_price ? `$${pos.current_price.toFixed(2)}` : '—'} />
                            <Detail label="BP Used" value={`$${(pos.buying_power_used / 1000).toFixed(1)}k`} />
                          </>
                        )}
                      </div>
                      {pos.stock_entry_price && (
                        <div className="mt-2 px-2.5 py-1.5 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className="text-10 md:text-9 px-1.5 py-0.5 bg-indigo-500/20 text-indigo-400 rounded font-bold">{pos.contracts * 100} SHARES</span>
                            <span className="text-10 text-slate-400">Bought @ ${pos.stock_entry_price.toFixed(2)}</span>
                            {pos.cost_basis && <span className="text-10 text-slate-500">• Basis: ${pos.cost_basis.toFixed(2)}</span>}
                          </div>
                          {isClosed && pos.exit_stock_price ? (
                            <div className="flex items-center gap-3 text-10">
                              <span className="text-slate-400">Sold @ <span className="text-white font-medium">${pos.exit_stock_price.toFixed(2)}</span></span>
                              {(() => {
                                const optionPnl = (pos.entry_price - (pos.exit_price || 0)) * pos.contracts * 100;
                                const stockPnl = (pos.exit_stock_price - pos.stock_entry_price!) * pos.contracts * 100;
                                const optUp = optionPnl >= 0;
                                const stkUp = stockPnl >= 0;
                                return <>
                                  <span className={optUp ? 'text-green-400' : 'text-red-400'}>{optUp ? '+' : '-'}${Math.abs(optionPnl).toFixed(0)} option</span>
                                  <span className={stkUp ? 'text-green-400' : 'text-red-400'}>{stkUp ? '+' : '-'}${Math.abs(stockPnl).toFixed(0)} stock</span>
                                </>;
                              })()}
                            </div>
                          ) : pos.current_stock_price ? (
                            <div className="flex items-center gap-2 text-10">
                              <span className="text-slate-400">Stock now: <span className="text-white font-medium">${pos.current_stock_price.toFixed(2)}</span></span>
                              {(() => {
                                const stockPnl = (pos.current_stock_price! - pos.stock_entry_price!) * pos.contracts * 100;
                                const isUp = stockPnl >= 0;
                                return <span className={isUp ? 'text-green-400' : 'text-red-400'}>{isUp ? '+' : '-'}${Math.abs(stockPnl).toFixed(0)} stock P&L</span>;
                              })()}
                            </div>
                          ) : null}
                        </div>
                      )}
                      <div className="mt-2 text-10 text-slate-500 leading-relaxed">
                        {isClosed ? (pos.exit_reason || 'Closed') : pos.entry_reason}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {activeTab === 'forum' && (
            <div className="space-y-3">
              {forumPosts.length === 0 ? (
                <EmptyState icon={<MessageSquare className="w-10 h-10 text-slate-600" />} message="No forum posts yet" />
              ) : (
                forumPosts.map((post) => <ForumPostItem key={post.id} post={post} agentId={agentId} />)
              )}
            </div>
          )}

          {activeTab === 'rules' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-400" /> Entry Rules
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <RuleItem label="Max Positions" value={agent.entry_rules.max_positions} />
                  <RuleItem label="Position Size" value={`${agent.entry_rules.max_position_size_pct}%`} />
                  <RuleItem label="Strategies" value={agent.entry_rules.allowed_strategies?.join(', ') || 'CSP, CC'} />
                  <RuleItem label="Market Hours" value={agent.entry_rules.require_market_hours ? 'Required' : 'Any Time'} />
                </div>
              </div>
              <div>
                <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-red-400" /> Exit Rules
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <RuleItem label="Take Profit" value={`${agent.exit_rules.take_profit_pct}%`} />
                  <RuleItem label="Stop Loss" value={`${agent.exit_rules.stop_loss_pct}%`} />
                  <RuleItem label="Close Before" value={`${agent.exit_rules.close_before_dte} DTE`} />
                  <RuleItem label="Max Hold" value={`${agent.exit_rules.max_hold_days} days`} />
                  <RuleItem label="Rolling" value={agent.exit_rules.allow_rolling ? 'Enabled' : 'Disabled'} />
                  {/* B-052: every agent may be assigned an in-the-money put it holds to expiry, not only the Wheel */}
                  {agent.exit_rules.allow_assignment && <RuleItem label="Assignment" value="Allowed" highlight />}
                </div>
              </div>
            </div>
          )}
        </div>
    </Modal>
  );
};

const StatCard: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({ icon, label, children }) => (
  <div className="p-3 bg-slate-800/40 rounded-xl border border-slate-700/20">
    <div className="flex items-center gap-1.5 text-slate-500 text-10 uppercase tracking-wider mb-1.5">{icon} {label}</div>
    {children}
  </div>
);

const Detail: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div><span className="text-slate-500">{label}:</span><span className="text-white ml-1 font-medium">{value}</span></div>
);

const RuleItem: React.FC<{ label: string; value: any; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className={`p-2.5 rounded-lg ${highlight ? 'bg-cyan-500/10 border border-cyan-500/20' : 'bg-slate-800/40 border border-slate-700/20'}`}>
    <div className="text-10 text-slate-500">{label}</div>
    <div className={`text-xs font-medium ${highlight ? 'text-cyan-400' : 'text-white'}`}>{value}</div>
  </div>
);

const EmptyState: React.FC<{ icon: React.ReactNode; message: string }> = ({ icon, message }) => (
  <div className="flex flex-col items-center justify-center py-12">{icon}<p className="text-xs text-slate-500 mt-2">{message}</p></div>
);

/* ===== P&L Distribution Bar Chart ===== */
const PnlDistributionChart: React.FC<{ closedPositions: Position[] }> = ({ closedPositions }) => {
  const buckets = useMemo(() => {
    if (closedPositions.length === 0) return [];
    const pnls = closedPositions.map(p => p.realized_pnl || 0);
    const min = Math.min(...pnls);
    const max = Math.max(...pnls);
    if (min === max) return [{ range: `$${min.toFixed(0)}`, count: pnls.length, avg: min }];

    const bucketCount = Math.min(8, Math.max(4, Math.ceil(pnls.length / 2)));
    const step = (max - min) / bucketCount;
    const result: { range: string; count: number; avg: number; }[] = [];

    for (let i = 0; i < bucketCount; i++) {
      const lo = min + i * step;
      const hi = lo + step;
      const inBucket = pnls.filter(v => i === bucketCount - 1 ? v >= lo && v <= hi : v >= lo && v < hi);
      const midpoint = (lo + hi) / 2;
      result.push({
        range: `${lo >= 0 ? '+' : '-'}$${Math.abs(lo).toFixed(0)}`,
        count: inBucket.length,
        avg: midpoint,
      });
    }
    return result;
  }, [closedPositions]);

  if (closedPositions.length < 2) return null;

  return (
    <div className="bg-slate-800/40 rounded-xl border border-slate-700/20 p-4">
      <h3 className="text-xs font-bold text-white mb-3">P&L Distribution</h3>
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={buckets} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
            <XAxis dataKey="range" stroke="#64748b" fontSize={9} tickLine={false} axisLine={false} />
            <YAxis stroke="#64748b" fontSize={9} tickLine={false} axisLine={false} allowDecimals={false} width={24} />
            <Tooltip
              contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', fontSize: '11px' }}
              formatter={(value: any) => [`${value} trades`, 'Count']}
            />
            <ReferenceLine x={buckets.findIndex(b => b.avg >= 0) > 0 ? buckets[buckets.findIndex(b => b.avg >= 0)].range : undefined} stroke="#475569" strokeDasharray="2 2" />
            <Bar dataKey="count" radius={[3, 3, 0, 0]}>
              {buckets.map((entry, i) => (
                <Cell key={i} fill={entry.avg >= 0 ? '#4ade80' : '#f87171'} fillOpacity={0.7} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="text-10 text-slate-500 mt-2 text-center">{closedPositions.length} closed trades</div>
    </div>
  );
};

/* ===== Drawdown Chart ===== */
const DrawdownChart: React.FC<{ agentId: string }> = ({ agentId }) => {
  const [drawdownData, setDrawdownData] = useState<{ date: string; drawdown: number }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDrawdown = async () => {
      try {
        const navData = await apiService.getAgentNavHistory(agentId, 'ALL');
        const history = navData.history || [];
        if (history.length < 2) { setDrawdownData([]); return; }

        // Backend serves drawdown_pct per point (agent_service.get_nav_history) — no client recompute.
        const data = history.map((point: any) => ({
          date: point.date,
          drawdown: point.drawdown_pct ?? 0,
        }));
        setDrawdownData(data);
      } catch {
        setDrawdownData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDrawdown();
  }, [agentId]);

  if (loading) return null;
  if (drawdownData.length < 2) return null;

  const maxDD = Math.min(...drawdownData.map(d => d.drawdown));

  return (
    <div className="bg-slate-800/40 rounded-xl border border-slate-700/20 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-bold text-white">Drawdown</h3>
        <span className="text-10 text-red-400 font-medium">
          Max: {maxDD.toFixed(2)}%
        </span>
      </div>
      <div className="h-32">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={drawdownData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={(d: string) => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              stroke="#64748b" fontSize={9} tickLine={false} axisLine={false} minTickGap={30}
            />
            <YAxis
              tickFormatter={(v: number) => `${v.toFixed(1)}%`}
              stroke="#64748b" fontSize={9} tickLine={false} axisLine={false} width={40}
              domain={['dataMin', 0]}
            />
            <ReferenceLine y={0} stroke="#475569" strokeDasharray="2 2" />
            <Tooltip
              contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', fontSize: '11px' }}
              labelFormatter={(label: any) => new Date(String(label) + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              formatter={(value: any) => [`${Number(value).toFixed(2)}%`, 'Drawdown']}
            />
            <Line type="monotone" dataKey="drawdown" stroke="#f87171" strokeWidth={1.5} dot={false} fill="#f87171" fillOpacity={0.1} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/** ROI% on capital at risk for a position card: realized when closed, unrealized when open. null
 *  when the API did not send it, rendered as "—", never a fabricated 0.0% (B-048, as AllOrdersPage). */
export const positionPnlPct = (pos: Position): number | null =>
  pos.status === 'closed' ? (pos.realized_roi_pct ?? null) : (pos.unrealized_roi_pct ?? null);

export const ForumPostItem: React.FC<{ post: ForumPost; agentId?: string }> = ({ post, agentId }) => {
  const [expanded, setExpanded] = useState(false);
  const typeColors: Record<string, string> = {
    cycle_discussion: 'bg-cyan-500/15 text-cyan-400',
    exit_discussion: 'bg-orange-500/15 text-orange-400',
  };
  const typeLabels: Record<string, string> = {
    cycle_discussion: 'DISCUSSION', exit_discussion: 'EXIT REVIEW',
  };
  const timeAgo = (dateStr: string) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    return `${Math.floor(diffHrs / 24)}d ago`;
  };
  // Every post is a cycle or exit thread (B-048 removed the legacy single-post rendering)
  const messages = post.messages || [];
  // If filtering by agent, show that agent's messages; otherwise show all
  const relevantMessages = agentId
    ? messages.filter((m: any) => m.agent_id === agentId)
    : messages;
  const threadSymbols = post.symbols || [];

  return (
    <div className="p-3 bg-slate-800/30 border border-slate-700/20 rounded-xl">
      <div className="flex items-center gap-2 mb-1.5">
        {threadSymbols.length > 0 && threadSymbols.map((s: string) => (
          <span key={s} className="text-10 px-1.5 py-0.5 bg-cyan-500/10 text-cyan-400 rounded font-mono">${s}</span>
        ))}
        <span className={`text-10 md:text-9 px-1.5 py-0.5 rounded font-bold ${typeColors[post.post_type] || ''}`}>
          {typeLabels[post.post_type] || post.post_type}
        </span>
        {messages.length > 0 && (
          <span className="text-10 text-slate-500">{messages.length} agents</span>
        )}
        <span className="text-10 text-slate-600 ml-auto">{timeAgo(post.created_at)}</span>
      </div>

      <div className="space-y-2">
        {debateInfluenceSummary(post.debate_influence) && (
          <div className="text-10 text-purple-400/80 flex items-center gap-1 mb-0.5">
            🔄 Debate influence: {debateInfluenceSummary(post.debate_influence)}
          </div>
        )}
        {relevantMessages.slice(0, expanded ? undefined : 2).map((msg: any, i: number) => (
          <div key={i} className="pl-2 border-l-2 border-slate-700/30">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-sm">{msg.agent_emoji}</span>
              <span className="text-10 font-semibold text-slate-300">{msg.agent_name}</span>
              {agentDebateBadge(post.debate_influence, msg.agent_id) && (
                <span className={`text-10 md:text-9 px-1 py-0.5 rounded ${
                  agentDebateBadge(post.debate_influence, msg.agent_id) === 'Held conviction'
                    ? 'bg-slate-600/20 text-slate-400' : 'bg-purple-500/15 text-purple-400'
                }`}>
                  {agentDebateBadge(post.debate_influence, msg.agent_id)}
                </span>
              )}
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">{msg.commentary}</p>
            {msg.decisions && msg.decisions.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {msg.decisions.map((d: any, j: number) => (
                  <span key={j} className={`text-10 md:text-9 px-1.5 py-0.5 rounded font-bold ${
                    d.decision === 'TAKE' ? 'bg-green-500/15 text-green-400' :
                    d.decision === 'CLOSE' ? 'bg-red-500/15 text-red-400' :
                    d.decision === 'HOLD' || d.decision === 'REDUCE' ? 'bg-yellow-500/15 text-yellow-400' :
                    'bg-slate-500/15 text-slate-400'
                  }`}>
                    {d.decision} {d.symbol}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
        {relevantMessages.length > 2 && (
          <button onClick={() => setExpanded(!expanded)} className="text-10 text-slate-500 hover:text-slate-300 flex items-center gap-1">
            <ChevronRight className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            {expanded ? 'Show less' : `+${relevantMessages.length - 2} more`}
          </button>
        )}
      </div>
    </div>
  );
};

/* ===== Statistical Significance (Spec 2.2) ===== */
export const SignificanceCard: React.FC<{ significance: SignificanceResult | null }> = ({ significance }) => {
  if (!significance) return null;
  const meta = significanceBadgeMeta(significance.badge);
  const a = significance.alpha_test;
  const s = significance.sharpe_bootstrap;
  const t = significance.trades_to_significance;

  return (
    <div className="bg-slate-800/40 rounded-xl border border-slate-700/20 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-3.5 h-3.5 text-cyan-400" />
          <h3 className="text-xs font-bold text-white">Statistical Significance</h3>
        </div>
        <span
          className={`text-10 px-2 py-0.5 rounded-full border font-medium ${meta.className}`}
          title={meta.description}
        >
          {meta.label}
        </span>
      </div>

      {significance.n_trades < 2 ? (
        <div className="text-11 text-slate-500">
          {significance.n_trades === 0 ? 'No closed trades yet.' : 'Need at least 2 closed trades.'}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-10 text-slate-500 uppercase tracking-wider mb-0.5">Mean / Trade</div>
            <div className={`text-sm font-bold tabular-nums ${(a.mean_return ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {a.mean_return === null ? '—' : `${a.mean_return >= 0 ? '+' : ''}${a.mean_return.toFixed(1)}%`}
            </div>
          </div>
          <div>
            <div className="text-10 text-slate-500 uppercase tracking-wider mb-0.5">p-value</div>
            <div className="text-sm font-bold text-white tabular-nums">{formatPValue(a.p_value)}</div>
          </div>
          <div>
            <div className="text-10 text-slate-500 uppercase tracking-wider mb-0.5">Sharpe 95% CI</div>
            <div className="text-sm font-bold text-white tabular-nums">
              {formatCI(s.ci_lower !== null && s.ci_upper !== null ? [s.ci_lower, s.ci_upper] : null)}
            </div>
            {s.sharpe_point_estimate !== null && (
              <div className="text-10 text-slate-500 tabular-nums">est {s.sharpe_point_estimate.toFixed(2)}</div>
            )}
          </div>
          <div>
            <div className="text-10 text-slate-500 uppercase tracking-wider mb-0.5">To Significance</div>
            <div className="text-sm font-bold text-white">
              {formatTradesToSignificance(t.current_trades, t.additional_trades_needed, t.already_significant)}
            </div>
          </div>
        </div>
      )}
      <div className="text-10 text-slate-600 mt-3">
        Based on {significance.n_trades} closed trade{significance.n_trades === 1 ? '' : 's'}.
      </div>
    </div>
  );
};

/* ===== Performance Attribution (Spec 2.5) ===== */
export const AttributionCard: React.FC<{ attribution: AttributionResult | null }> = ({ attribution }) => {
  if (!attribution) return null;
  if (!hasAttributionData(attribution.attribution)) {
    return (
      <div className="bg-slate-800/40 rounded-xl border border-slate-700/20 p-4">
        <div className="flex items-center gap-2 mb-1">
          <BarChart3 className="w-3.5 h-3.5 text-cyan-400" />
          <h3 className="text-xs font-bold text-white">Performance Attribution</h3>
        </div>
        <div className="text-11 text-slate-500">No closed trades to attribute yet.</div>
      </div>
    );
  }

  // Lead with the most actionable dimensions; skip any that are empty.
  const order = ['strategy', 'regime', 'dte', 'condition', 'consensus', 'quarter'];
  const dims = order.filter((d) => Object.keys(attribution.attribution[d] || {}).length > 0);

  return (
    <div className="bg-slate-800/40 rounded-xl border border-slate-700/20 p-4 space-y-4">
      <div className="flex items-center gap-2">
        <BarChart3 className="w-3.5 h-3.5 text-cyan-400" />
        <h3 className="text-xs font-bold text-white">Performance Attribution</h3>
        <span className="text-10 text-slate-500 ml-auto">{attribution.total_trades} closed trades · ROI%</span>
      </div>
      {dims.map((dim) => {
        const rows = sortBuckets(attribution.attribution[dim]);
        return (
          <div key={dim}>
            <div className="text-10 text-slate-500 uppercase tracking-wider mb-1.5">
              {DIMENSION_LABELS[dim] || dim}
            </div>
            <div className="space-y-1">
              {/* header */}
              {/* mobile-ok: 5/2/2/3 split of a truncating label + three short numerics */}
              <div className="grid grid-cols-12 gap-2 text-10 md:text-9 text-slate-600 uppercase tracking-wider px-2">
                <div className="col-span-5">Bucket</div>
                <div className="col-span-2 text-right">Trades</div>
                <div className="col-span-2 text-right">Win%</div>
                <div className="col-span-3 text-right">Avg ROI</div>
              </div>
              {rows.map((r) => (
                // mobile-ok: matches the header split above; label truncates
                <div key={r.key} className="grid grid-cols-12 gap-2 items-center px-2 py-1 rounded-lg bg-slate-900/30">
                  <div className="col-span-5 text-11 text-slate-300 truncate">{prettyBucketKey(r.key)}</div>
                  <div className="col-span-2 text-right text-11 text-slate-400 tabular-nums">{r.trades}</div>
                  <div className={`col-span-2 text-right text-11 tabular-nums ${winRateColor(r.win_rate)}`}>
                    {r.win_rate.toFixed(0)}%
                  </div>
                  <div className={`col-span-3 text-right text-11 tabular-nums ${r.avg_pnl_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {r.avg_pnl_pct >= 0 ? '+' : ''}{r.avg_pnl_pct.toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ===== Agent Evolution Time-Series (Spec 2.4) ===== */
export const EvolutionCard: React.FC<{ evolution: AgentEvolution | null }> = ({ evolution }) => {
  const [metric, setMetric] = useState('rolling_win_rate');
  if (!evolution) return null;
  if (!hasEvolutionData(evolution)) {
    return (
      <div className="bg-slate-800/40 rounded-xl border border-slate-700/20 p-4">
        <div className="flex items-center gap-2 mb-1">
          <Activity className="w-3.5 h-3.5 text-cyan-400" />
          <h3 className="text-xs font-bold text-white">Agent Evolution</h3>
        </div>
        <div className="text-11 text-slate-500">
          No evolution history yet — snapshots accumulate one per trading day.
        </div>
      </div>
    );
  }

  const options = ['rolling_win_rate', 'rolling_sharpe', 'avg_confidence', 'skip_rate', 'capital_utilization_pct'];
  const series = extractSeries(evolution.data_points, metric).filter((p) => p.value !== null);
  const trend = metricTrend(evolution.data_points, metric);

  return (
    <div className="bg-slate-800/40 rounded-xl border border-slate-700/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="w-3.5 h-3.5 text-cyan-400" />
        <h3 className="text-xs font-bold text-white">Agent Evolution</h3>
        <select
          value={metric}
          onChange={(e) => setMetric(e.target.value)}
          className="ml-auto text-10 bg-slate-900/60 border border-slate-700/40 rounded px-1.5 py-0.5 text-slate-300"
        >
          {options.map((m) => <option key={m} value={m}>{metricLabel(m)}</option>)}
        </select>
      </div>
      {series.length < 2 ? (
        <div className="text-11 text-slate-500">Need at least 2 days of data to chart {metricLabel(metric)}.</div>
      ) : (
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis
                dataKey="date" stroke="#64748b" fontSize={9} tickLine={false} axisLine={false} minTickGap={30}
                tickFormatter={(d: string) => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis stroke="#64748b" fontSize={9} tickLine={false} axisLine={false} width={32} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', fontSize: '11px' }}
                labelFormatter={(l: any) => new Date(String(l) + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                formatter={(v: any) => [Number(v).toFixed(1), metricLabel(metric)]}
              />
              <Line type="monotone" dataKey="value" stroke="#22d3ee" strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="text-10 text-slate-600 mt-2 flex items-center justify-center gap-2">
        <span>{evolution.data_points.length} daily snapshot{evolution.data_points.length === 1 ? '' : 's'} · {evolution.period}</span>
        {trend !== null && (
          <span className={trend > 0 ? 'text-green-500' : trend < 0 ? 'text-red-500' : 'text-slate-500'}>
            {trend > 0 ? '▲' : trend < 0 ? '▼' : '—'} {trend > 0 ? '+' : ''}{trend} since start
          </span>
        )}
      </div>
    </div>
  );
};

/* ===== Identity Version History (Spec 2.1) ===== */
export const IdentityHistoryView: React.FC<{ identity: IdentityHistory | null }> = ({ identity }) => {
  if (!identity || identity.history.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500 text-sm">
        No identity history yet. Versions are archived during weekly memory consolidation.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-11 text-slate-500">
        {identity.history.length} version{identity.history.length === 1 ? '' : 's'}
        {identity.current_version !== null ? ` · current is v${identity.current_version}` : ''}
      </div>
      {identity.history.map((v, i) => (
        <div
          key={v.version ?? i}
          className={`p-4 rounded-xl border ${
            v.is_current
              ? 'bg-cyan-500/5 border-cyan-500/30'
              : 'bg-slate-800/40 border-slate-700/20'
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-bold text-white">v{v.version}</span>
            {v.is_current && (
              <span className="text-10 md:text-9 px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 font-bold">
                CURRENT
              </span>
            )}
            <span className="text-10 text-slate-500 ml-auto">
              {formatDateWithYear(v.is_current ? v.last_consolidated : (v.archived_at || v.last_consolidated))}
            </span>
          </div>

          {v.personality_summary && (
            <p className="text-xs text-slate-300 leading-relaxed mb-2">{v.personality_summary}</p>
          )}

          {v.personality_metrics && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-10 text-slate-500 mb-2 tabular-nums">
              <span>{v.personality_metrics.principles_count} principles</span>
              <span>{v.personality_metrics.weaknesses_count} weaknesses</span>
              <span>{v.personality_metrics.total_trades} trades</span>
              <span>{v.personality_metrics.win_rate_at_consolidation.toFixed(0)}% WR</span>
              <span>
                {v.personality_metrics.avg_pnl_at_consolidation >= 0 ? '+' : ''}
                {v.personality_metrics.avg_pnl_at_consolidation.toFixed(1)}% avg
              </span>
            </div>
          )}

          {v.core_principles && v.core_principles.length > 0 && (
            <div className="mb-2">
              <div className="text-10 text-slate-500 uppercase tracking-wider mb-1">Principles</div>
              <ul className="space-y-0.5">
                {v.core_principles.map((p, i) => (
                  <li key={i} className="text-11 text-slate-400 leading-snug">• {p}</li>
                ))}
              </ul>
            </div>
          )}

          {v.known_weaknesses && v.known_weaknesses.length > 0 && (
            <div>
              <div className="text-10 text-slate-500 uppercase tracking-wider mb-1">Known Weaknesses</div>
              <ul className="space-y-0.5">
                {v.known_weaknesses.map((w, i) => (
                  <li key={i} className="text-11 text-yellow-400/70 leading-snug">⚠ {w}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default AgentDetailModal;
